import React from "react";
import { Info, AlertTriangle, CheckCircle2, XOctagon } from "lucide-react";

const CALLOUT_STYLES = {
  info: {
    border: "border-teal-400/25",
    bg: "bg-teal-400/[0.06]",
    icon: <Info className="text-teal-300" size={18} />,
    title: "text-teal-200",
  },
  warn: {
    border: "border-amber-400/30",
    bg: "bg-amber-400/[0.06]",
    icon: <AlertTriangle className="text-amber-300" size={18} />,
    title: "text-amber-200",
  },
  success: {
    border: "border-emerald-400/25",
    bg: "bg-emerald-400/[0.06]",
    icon: <CheckCircle2 className="text-emerald-300" size={18} />,
    title: "text-emerald-200",
  },
  danger: {
    border: "border-rose-400/30",
    bg: "bg-rose-400/[0.07]",
    icon: <XOctagon className="text-rose-300" size={18} />,
    title: "text-rose-200",
  },
};

const Block = ({ block }) => {
  switch (block.type) {
    case "p":
      return (
        <p className="text-gray-300 leading-relaxed text-base">{block.text}</p>
      );
    case "h2":
      return (
        <h2 className="font-display font-semibold text-2xl md:text-[1.65rem] text-white pt-6">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-medium text-lg text-white pt-2">{block.text}</h3>
      );
    case "ul":
      return (
        <ul className="list-disc pl-6 space-y-2 text-gray-300 leading-relaxed">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-6 space-y-2 text-gray-300 leading-relaxed">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    case "code":
      return (
        <pre className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 overflow-x-auto text-xs text-gray-100 leading-relaxed font-mono">
          {block.lang && (
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              {block.lang}
            </div>
          )}
          <code>{block.text}</code>
        </pre>
      );
    case "callout": {
      const style = CALLOUT_STYLES[block.variant || "info"];
      return (
        <div
          className={`flex gap-3 rounded-xl border ${style.border} ${style.bg} p-4`}
        >
          <div className="flex-shrink-0 pt-0.5">{style.icon}</div>
          <div className="text-sm text-gray-200 leading-relaxed">
            {block.title && (
              <div className={`font-semibold mb-1 ${style.title}`}>
                {block.title}
              </div>
            )}
            <div>{block.text}</div>
          </div>
        </div>
      );
    }
    case "kv":
      return (
        <div className="rounded-xl border border-white/[0.08] overflow-hidden">
          {block.title && (
            <div className="bg-white/[0.04] px-4 py-2 text-xs uppercase tracking-wider text-gray-400 font-semibold border-b border-white/[0.06]">
              {block.title}
            </div>
          )}
          <div>
            {block.items.map(([k, v], i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 px-4 py-3 border-b border-white/[0.05] last:border-b-0 text-sm"
              >
                <div className="text-white font-medium">{k}</div>
                <div className="text-gray-300">{v}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case "image":
      return (
        <figure
          className="rounded-xl overflow-hidden border border-white/[0.08] bg-black/40"
          data-testid={`article-image-${(block.src || "").split("/").pop()}`}
        >
          <img
            src={block.src}
            alt={block.alt || block.caption || ""}
            className="w-full h-auto block"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="px-4 py-2.5 text-xs text-gray-400 border-t border-white/[0.06] bg-white/[0.02]">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case "video":
      return (
        <figure
          className="rounded-xl overflow-hidden border border-white/[0.08] bg-black/40 ring-1 ring-teal-400/10"
          data-testid={`article-video-${(block.sources?.[0]?.src || block.src || "").split("/").pop()}`}
        >
          <video
            className="w-full h-auto block"
            poster={block.poster}
            autoPlay={block.autoplay !== false}
            loop={block.loop !== false}
            muted
            playsInline
            controls={block.controls === true}
            preload="metadata"
          >
            {(block.sources || (block.src ? [{ src: block.src, type: "video/mp4" }] : [])).map((s, i) => (
              <source key={i} src={s.src} type={s.type} />
            ))}
          </video>
          {block.caption && (
            <figcaption className="px-4 py-2.5 text-xs text-gray-400 border-t border-white/[0.06] bg-white/[0.02]">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
};

export const ArticleRenderer = ({ blocks }) => {
  return (
    <div className="space-y-5">
      {blocks.map((b, i) => (
        <Block key={i} block={b} />
      ))}
    </div>
  );
};

export default ArticleRenderer;
